import { useState, useEffect, useCallback } from 'react'
import GenericTable from '../GenericTable'
import { d1Query, d1Insert, d1Update, d1Delete } from '../../services/d1Service'
import type { ImportantLink, StatusMessage } from '../../types'

interface Props { eiin: string; onStatus: (msg: StatusMessage) => void }

export default function ImportantLinksTab({ eiin, onStatus }: Props) {
  const [items, setItems] = useState<ImportantLink[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Partial<ImportantLink> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setItems(await d1Query<ImportantLink>(`SELECT * FROM important_links WHERE institution_eiin = ? ORDER BY sort_order ASC`, [eiin])) }
    catch (e: any) { onStatus({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }, [eiin])

  useEffect(() => { load() }, [load])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editing) return
    try {
      const rec = { ...editing, institution_eiin: eiin }
      if ((editing as any).id) await d1Update('important_links', (editing as any).id, rec)
      else await d1Insert('important_links', rec)
      onStatus({ type: 'success', text: 'Link saved!' })
      setEditing(null); load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  const toggleActive = async (row: ImportantLink) => {
    try {
      const newStatus = row.is_active === 0 ? 1 : 0
      await d1Update('important_links', row.id!, { is_active: newStatus })
      onStatus({ type: 'success', text: `Link ${newStatus ? 'activated' : 'deactivated'}!` })
      load()
    } catch (e: any) { onStatus({ type: 'error', text: e.message }) }
  }

  return (
    <GenericTable
      title="Important Links" icon="🔗"
      items={items} loading={loading} editingItem={editing}
      columns={[
        { key: 'title_bn', label: 'Title (BN)', width: '200px' },
        { key: 'title_en', label: 'Title (EN)', width: '200px' },
        { key: 'url', label: 'URL', width: '220px', render: (v) => <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: '12px' }}>{v}</a> },
        { key: 'sort_order', label: 'Order', width: '60px' },
        {
          key: 'is_active', label: 'Status', width: '100px',
          render: (v, row) => (
            <button
              onClick={(e) => { e.stopPropagation(); toggleActive(row) }}
              style={{
                padding: '3px 10px', borderRadius: '12px', border: 'none', fontWeight: 700, fontSize: '11px', cursor: 'pointer',
                background: v === 0 ? '#fee2e2' : '#dcfce7',
                color: v === 0 ? '#dc2626' : '#16a34a'
              }}
            >
              {v === 0 ? '🔴 Deactive' : '🟢 Active'}
            </button>
          )
        }
      ]}
      formFields={[
        { key: 'title_bn', label: 'Title (BN)', required: true },
        { key: 'title_en', label: 'Title (EN)', required: true },
        { key: 'url', label: 'URL', required: true },
        { key: 'sort_order', label: 'Sort Order', type: 'number' },
        { key: 'is_active', label: 'Status', type: 'select', options: [
          { value: '1', label: '🟢 Active' },
          { value: '0', label: '🔴 Deactive' }
        ]}
      ]}
      onAdd={() => setEditing({ title_bn: '', title_en: '', url: '', sort_order: 1, is_active: 1 })}
      onEdit={(item) => setEditing({ ...item })}
      onDelete={async (id) => { try { await d1Delete('important_links', id); onStatus({ type: 'success', text: 'Deleted!' }); load() } catch (e: any) { onStatus({ type: 'error', text: e.message }) } }}
      onSave={handleSave}
      onCancel={() => setEditing(null)}
      onFieldChange={(key, val) => setEditing(prev => prev ? { ...prev, [key]: val } : prev)}
    />
  )
}
